<script setup lang="ts">
import { helpers, maxValue, minValue } from '@vuelidate/validators'
import { useVModel } from '@vueuse/core'
import { DateTime } from 'luxon'
import type { ToastMessageOptions } from 'primevue/toast'

import type { Vehicle } from '~/layers/forms/models'
import { AuxiliarMeta, Form } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Vehicle
  preFilled?: boolean
  asCriteria?: boolean
  showAsistance?: boolean
  validationScope?: string
  forMoto?: boolean
  useVersion?: boolean
  argentoInsuranceCompanyId?: number | null
  argentoInsuranceProductId?: number | null
  argentoInsurancebrokerProductId?: number | null
  coverageTypeCode?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  validationScope: Form.vehicle,
  argentoInsuranceCompanyId: null,
  argentoInsuranceProductId: null,
  argentoInsurancebrokerProductId: null,
  coverageTypeCode: null,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)
const toast = useToast()
const { constraints } = useContractConstraints()
const { LocalConfigurationStore, RemoteConfigurationStore } = stores()
const showAsistance = computed(() => props.showAsistance && props.asCriteria)

const coverageConfig = computed(() => {
  const { planParameters } = LocalConfigurationStore.value ?? {}
  const { coverageTypeCode } = props

  if (planParameters?.branch === 'Auto' && coverageTypeCode)
    return (
      planParameters.config.find((c) => c.coverageTypeOrPlanCodes.includes(coverageTypeCode)) ??
      null
    )
  return null
})

const years = computed(() => {
  const years: { label: string; value: number }[] = []
  const date = DateTime.fromJSDate(new Date()).year + 1
  for (
    let i = 0;
    i <= (coverageConfig?.value?.minVehicleYear ?? constraints.value?.minVehicleYear ?? 10);
    i++
  ) {
    years.push({ label: (date - i).toString(), value: date - i })
  }
  return years
})

const v$ = useValidation(
  () => ({
    manufacturer: {
      selected: helpers.withMessage('Seleccione una marca.', () => !!model.value.manufacturer?.id),
    },
    model: {
      selected: helpers.withMessage('Seleccione un modelo.', () => !!model.value.model?.id),
    },
    version: {
      selected: helpers.withMessage('Seleccione una verión.', () =>
        props.useVersion ? !!model.value.version?.id : true
      ),
    },
    year: {
      required: requiredValidator,
      maxValue: helpers.withMessage(
        ({ $params }) => `El año máximo permitido es ${$params.max}`,
        maxValue(DateTime.fromJSDate(new Date()).year + 1)
      ),
      minValue: helpers.withMessage(
        ({ $params }) => `El año mínimo permitido es ${$params.min}`,
        minValue(DateTime.fromJSDate(new Date()).year - 35)
      ),
    },
    use: {
      selected: helpers.withMessage('Seleccione un uso.', () => !!model.value.use?.id),
    },
  }),
  model,
  {
    $scope: props.validationScope,
    $registerAs: Form.vehicle,
  }
)

const { builder: dispatcherParamsBuilder, argentoInsuranceProps } =
  useSiteApiDispatcherByProductParams({
    argentoInsuranceProps: computed(() => ({
      emisorId:
        props.argentoInsuranceCompanyId ?? RemoteConfigurationStore.value?.emisor?.id ?? undefined,
      productoId:
        props.argentoInsuranceProductId ??
        RemoteConfigurationStore.value?.producto?.id ??
        undefined,
      brokerProductoId:
        props.argentoInsurancebrokerProductId ??
        RemoteConfigurationStore.value?.brokerProductoId ??
        undefined,
    })),
  })

const {
  execute: fetchBrands,
  data: manufacturers,
  status: manufacturersStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/VehiculoMarcas',
    options: { immediate: !props.preFilled },
  })
)

const getBrands = () => {
  if (!props.preFilled) fetchBrands()
}

const {
  execute: fetchModels,
  data: models,
  status: modelsStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/VehiculoModelos',
    body: computed<SiteApiSchema<'VehiculoModeloDispatcherItemModel'>>(() => ({
      marcaId: model.value.manufacturer?.id,
    })),
  })
)

const getModels = () => {
  if (!props.preFilled && model.value.manufacturer?.id) fetchModels()
}

const {
  execute: fetchVersions,
  data: versions,
  status: versionsStatus,
  clear: clearVersions,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/VehiculoVersionesByCatalog',
    body: computed<SiteApiSchema<'VehiculoVersionDispatcherItemModel'>>(() => ({
      marcaId: model.value.manufacturer?.id,
      modeloId: model.value.model?.id,
    })),
  })
)

const canFetchVersions = computed(() =>
  [
    props.useVersion,
    argentoInsuranceProps.value.brokerProductoId,
    argentoInsuranceProps.value.emisorId,
    argentoInsuranceProps.value.brokerProductoId,
    model.value.manufacturer?.id,
    model.value.model?.id,
  ]
    .map(Boolean)
    .every((i) => i === true)
)

const getVersions = () => {
  if (canFetchVersions.value) fetchVersions()
}

const {
  execute: fetchUses,
  data: uses,
  status: usesStatus,
} = useSiteApi(
  ...dispatcherParamsBuilder({
    endpoint: '/api/Auxiliares/ByProducto/VehiculoUsos',
    body: computed<SiteApiSchema<'VehiculoUsoDispatcherItemModel'>>(() => ({
      modeloId: model.value.model?.id,
    })),
  })
)

const getUses = () => {
  if (!props.preFilled) fetchUses()
}

watch(
  () => model.value.manufacturer?.id,
  (n, o) => {
    if (n === o) return
    model.value.model = AuxiliarMeta.load()
    model.value.version = AuxiliarMeta.load()
    getModels()
    const manufacturer = manufacturers.value?.items?.find((item) => item.id === n)
    if (manufacturer?.id)
      model.value.manufacturer = {
        id: manufacturer.id,
        code: manufacturer?.codigo,
        name: manufacturer?.nombre,
      }
  }
)

watch(
  () => model.value.model?.id,
  (n, o) => {
    model.value.use = AuxiliarMeta.load()
    model.value.version = AuxiliarMeta.load()
    if (!n || n === o) return
    if (!props.preFilled) {
      getUses()
      getVersions()
    }
    const _model = models.value?.items?.find((item) => item.id === n)
    if (_model?.id)
      model.value.model = { id: _model.id, code: _model?.codigo, name: _model?.nombre }
  }
)

watch(
  () => model.value.version?.id,
  (n, o) => {
    if (!n || n === o) return
    const version = versions?.value?.items?.find((item) => item.id === n)
    if (version?.id)
      model.value.version = {
        id: version.id ?? null,
        code: version?.codigo,
        name: version?.nombre,
      }
  }
)

watch(
  () => model.value.use?.id,
  (n, o) => {
    if (!n || n === o) return
    const use = uses.value?.items?.find((item) => item.id === n)
    if (use?.id) model.value.use = { id: use?.id, code: use?.codigo, name: use?.nombre }
  }
)

watch(
  computed(() => ({
    manufacturer: model.value.manufacturer?.id,
    model: model.value.model?.id,
    productoId: argentoInsuranceProps.value.productoId,
    emisorId: argentoInsuranceProps.value.emisorId,
    brokerProductoId: argentoInsuranceProps.value.brokerProductoId,
  })),
  () => {
    if (props.useVersion) {
      clearVersions()
      getVersions()
      model.value.version = AuxiliarMeta.load()
    }
  },
  {
    deep: true,
  }
)

const formContainerId = computed(() => `${Form.vehicle}${props.asCriteria ? '-criteria' : ''}-form`)

const useDisclaimer: ToastMessageOptions = {
  summary: 'Uso del vehículo',
  severity: 'info',
  detail:
    'Las aseguradoras pueden rechazar tu reclamación si declaras uso privado cuando en realidad es taxi, Uber o renta.',
}

const displayUseDisclaimer = () => {
  toast.remove(useDisclaimer)
  if (model.value.use?.name?.toLowerCase().includes('privado')) toast.add(useDisclaimer)
}
</script>

<template>
  <FormsGlobalContainer :identifier="formContainerId">
    <FormsGlobalTitle
      text="Información del vehículo"
      icon-class="icon-[ph--car-bold]"
      :col-span-full="$viewport.isLessThan('md') || !showAsistance"
      :class="{
        'col-span-2': $viewport.isGreaterOrEquals('md') && showAsistance,
      }"
    />

    <div
      v-if="showAsistance"
      :class="{
        'flex items-center justify-end': $viewport.isGreaterOrEquals('md'),
        'order-last flex justify-stretch my-[-10px]': $viewport.isLessThan('md'),
      }"
    >
      <FormsGlobalAssistancePanel
        heading="¿No encuentras tu vehículo?"
        text="Contáctanos y comparte tus datos para recibir asistencia."
        button-text="No encuentro mi vehículo"
        as-dialog
        :fluid-button="$viewport.isLessThan('md')"
      />
    </div>

    <InputField
      v-if="model.manufacturer"
      label="Marca"
      :error="!props.preFilled && v$.manufacturer?.$error"
      :error-message="v$.manufacturer?.$errors[0]?.$message.toString()"
      :loading="manufacturersStatus === 'pending'"
    >
      <InputGroupRetry
        v-if="!props.preFilled"
        :retry="!manufacturers?.items?.length"
        @retry="getBrands()"
      >
        <PvSelect
          v-model="model.manufacturer.id"
          option-value="id"
          option-label="nombre"
          :options="manufacturers?.items ?? []"
          :disabled="props.preFilled"
        />
      </InputGroupRetry>
      <PvInputText
        v-else
        v-model="model.manufacturer.name"
        no-hint
        disabled
      />
    </InputField>

    <InputField
      v-if="model.model"
      label="Modelo"
      :error="!props.preFilled && v$.model?.$error"
      :error-message="v$.model?.$errors[0]?.$message.toString()"
      :loading="modelsStatus === 'pending'"
    >
      <InputGroupRetry
        v-if="!props.preFilled"
        :retry="!models?.items?.length && !!model.manufacturer?.id"
        @retry="getModels()"
      >
        <PvSelect
          v-model="model.model.id"
          option-value="id"
          option-label="nombre"
          :options="models?.items ?? []"
          :disabled="!model.manufacturer?.id"
        />
      </InputGroupRetry>
      <PvInputText
        v-else
        v-model="model.model.name"
        no-hint
        disabled
      />
    </InputField>

    <template v-if="!asCriteria && !forMoto">
      <InputField
        v-if="useVersion"
        label="Versión"
        :error="v$.version?.$error"
        :error-message="v$.version?.$errors[0]?.$message.toString()"
        :loading="versionsStatus === 'pending'"
      >
        <InputGroupRetry
          :retry="!versions?.items?.length && !!model.model?.id"
          @retry="getVersions()"
        >
          <PvSelect
            v-if="model.version"
            v-model="model.version.id"
            option-value="id"
            option-label="nombre"
            :options="versions?.items ?? []"
            :disabled="!model.model?.id"
          />
        </InputGroupRetry>
      </InputField>
    </template>

    <InputField
      label="Año"
      :error="!props.preFilled && v$.year?.$error"
      :error-message="v$.year?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-if="!props.preFilled"
        v-model="model.year"
        :options="years"
        option-value="value"
        option-label="label"
        :disabled="preFilled"
      />
      <PvInputNumber
        v-else-if="props.preFilled"
        v-model="model.year"
        :format="false"
        no-hint
        disabled
      />
    </InputField>

    <InputField
      v-if="model.use"
      label="Uso"
      :error="!props.preFilled && v$.use?.$error"
      :error-message="v$.use?.$errors[0]?.$message.toString()"
      :loading="usesStatus === 'pending'"
    >
      <InputGroupRetry
        v-if="!props.preFilled"
        :retry="!uses?.items?.length && !!model.model?.id"
        @retry="getUses()"
      >
        <PvSelect
          v-model="model.use.id"
          option-value="id"
          option-label="nombre"
          :options="uses?.items ?? []"
          :disabled="!model.model?.id"
          @hide="displayUseDisclaimer()"
        />
      </InputGroupRetry>
      <PvInputText
        v-else-if="props.preFilled"
        v-model="model.use.name"
        no-hint
        disabled
      />
    </InputField>

    <FormsGlobalCurrencyInput
      v-model="model.amount"
      :validation-scope="validationScope ?? Form.vehicle"
      default-currency="DominicanPeso"
      label="Valor del vehículo"
      :disabled="preFilled"
    />

    <template v-if="asCriteria">
      <slot name="aditional-fields" />
      <InputField label=" ">
        <slot name="action" />
      </InputField>
    </template>
  </FormsGlobalContainer>
</template>
